import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldQuestion,
  Trash2,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type AssessmentTemplatePublic,
  type AssessmentTemplateStatus,
} from "@eduviaos/shared";
import { mapTemplateStatusToPill } from "@/lib/assessments/statusPill";
import { AssessmentTemplateCreateSheet } from "@/components/assessments/AssessmentTemplateCreateSheet";
import {
  useActivateAssessmentTemplate,
  useArchiveAssessmentTemplate,
  useAssessmentTemplates,
} from "@/hooks/useAssessments";
import { useAuth } from "@/auth/AuthProvider";
import { useCanAuthorAssessments } from "@/hooks/useAssessmentAuthoring";
import { TenantAuthoringPausedBanner } from "@/components/assessments/TenantAuthoringPausedBanner";

/**
 * Phase H5 FE-1 — IA Assessments → Templates.
 *
 * DataTable + create / activate / archive over `/assessments/templates`.
 * Detail and blueprint editing live at
 * `/assessments/templates/:slug` (rendered by `AssessmentTemplateDetailPage`).
 *
 * Activate is intentionally surfaced from the list (in addition to the
 * detail page) so an SA who edits a template's blueprint and steps away
 * can flip it active again from the list view once the bank catches up.
 */

export function AssessmentTemplatesPage() {
  const navigate = useNavigate();
  const [qInput, setQInput] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<AssessmentTemplateStatus | "__any__">(
    "__any__",
  );
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [pendingActivate, setPendingActivate] = React.useState<AssessmentTemplatePublic | null>(
    null,
  );
  const [activateNote, setActivateNote] = React.useState("");
  const [activateError, setActivateError] = React.useState<string | null>(null);
  const [pendingArchive, setPendingArchive] = React.useState<AssessmentTemplatePublic | null>(null);
  const [archiveReason, setArchiveReason] = React.useState("");
  const [archiveError, setArchiveError] = React.useState<string | null>(null);

  const { data, isLoading, isFetching, refetch, isError, error } = useAssessmentTemplates({
    search: qInput.trim() || undefined,
    status: statusFilter === "__any__" ? undefined : statusFilter,
    include_archived: includeArchived || statusFilter === "archived",
    limit: 200,
  });
  const activateMutation = useActivateAssessmentTemplate();
  const archiveMutation = useArchiveAssessmentTemplate();

  const { user } = useAuth();
  // Phase H5d — combine RBAC (`manage_question_bank`) with the
  // per-tenant `assessments_authoring_by_tenant` flag. `canWrite`
  // collapses both signals so existing UI conditionals stay tight; the
  // hook also exposes `pausedByTenant` so we can show the banner that
  // explains WHY the controls are missing when RBAC alone would have
  // permitted them.
  const { canAuthor: canWrite, missingRbac, pausedByTenant } = useCanAuthorAssessments();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleConfirmActivate = async () => {
    if (!pendingActivate) return;
    setActivateError(null);
    try {
      await activateMutation.mutateAsync({
        slug: pendingActivate.slug,
        note: activateNote.trim() || undefined,
      });
      toast.success(`Activated "${pendingActivate.name}".`);
      setPendingActivate(null);
      setActivateNote("");
    } catch (raw) {
      setActivateError(extractMessage(raw, "Could not activate — check the blueprint."));
    }
  };

  const handleConfirmArchive = async () => {
    if (!pendingArchive) return;
    setArchiveError(null);
    if (archiveReason.trim().length < 3) {
      setArchiveError("Reason must be at least 3 characters.");
      return;
    }
    try {
      await archiveMutation.mutateAsync({
        slug: pendingArchive.slug,
        reason: archiveReason.trim(),
      });
      toast.success(`Archived "${pendingArchive.name}".`);
      setPendingArchive(null);
      setArchiveReason("");
    } catch (raw) {
      setArchiveError(extractMessage(raw, "Could not archive — please try again."));
    }
  };

  return (
    <div className="mx-auto max-w-7xl py-8" data-testid="templates-list">
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/assessments">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Assessments
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Templates</PageHeaderHeading>
            <PageHeaderDescription>
              Reusable blueprints + runtime config. Bind a template's slug to an admission stage
              requirement to deliver it to applicants.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
              aria-label="Refresh templates"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            {canWrite ? (
              <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="templates-create">
                <Plus aria-hidden="true" className="size-4" />
                New template
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {missingRbac && user ? (
        <div
          role="status"
          className="border-border/60 bg-muted/40 text-muted-foreground mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <ShieldQuestion
            aria-hidden="true"
            className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
          />
          <span className="flex-1">
            Read-only view. Authoring + activating templates needs{" "}
            <code className="text-foreground">manage_question_bank</code>.
          </span>
        </div>
      ) : null}

      {pausedByTenant ? <TenantAuthoringPausedBanner className="mt-4" /> : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn't load templates
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
        </div>
      ) : null}

      <DataTable className="mt-6">
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Filter by name…"
              className="pl-8"
              aria-label="Filter templates"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
          >
            <SelectTrigger className="h-9 w-36" aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Checkbox
              id="templates-include-archived"
              checked={includeArchived}
              onCheckedChange={(v) => setIncludeArchived(v === true)}
            />
            <Label
              htmlFor="templates-include-archived"
              className="text-muted-foreground text-xs font-normal"
            >
              Show archived
            </Label>
          </div>
          {qInput || statusFilter !== "__any__" ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQInput("");
                setStatusFilter("__any__");
              }}
            >
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[38%]">Name</TableHead>
                <TableHead className="w-[8%]">Grades</TableHead>
                <TableHead className="w-[8%]">Lang</TableHead>
                <TableHead className="w-[9%]">Items</TableHead>
                <TableHead className="w-[9%] text-right">Total marks</TableHead>
                <TableHead className="w-[10%]">Mode</TableHead>
                <TableHead className="w-[9%]">Status</TableHead>
                <TableHead className="w-[9%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={8} />
            ) : (
              <TableBody>
                {items.map((t) => (
                  <TemplateRow
                    key={t.id}
                    template={t}
                    canWrite={canWrite}
                    onOpen={(slug) => navigate(`/assessments/templates/${slug}`)}
                    onActivate={(target) => {
                      setActivateError(null);
                      setActivateNote("");
                      setPendingActivate(target);
                    }}
                    onArchive={(target) => {
                      setArchiveError(null);
                      setArchiveReason("");
                      setPendingArchive(target);
                    }}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<ClipboardList aria-hidden="true" />}
            title={qInput || statusFilter !== "__any__" ? "No matches" : "No templates yet"}
            description={
              qInput || statusFilter !== "__any__"
                ? "Adjust filters or clear them to see every template."
                : "A template is a blueprint + runtime config. Once active, it can be bound to an admission stage requirement."
            }
            action={
              qInput || statusFilter !== "__any__" ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setQInput("");
                    setStatusFilter("__any__");
                  }}
                >
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : canWrite ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus aria-hidden="true" className="size-4" />
                  New template
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && items.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              {items.length.toLocaleString()} shown
            </span>
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">Activate gate:</strong> a template can only be
          activated when its blueprint is fulfillable — every item has enough approved questions
          available. The blueprint editor on the detail page shows the per-item deficit, so you can
          fix the gap before activation.
        </CardContent>
      </Card>

      <AssessmentTemplateCreateSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(t) => {
          toast.success(`Created "${t.name}". Author its blueprint next.`);
          navigate(`/assessments/templates/${t.slug}`);
        }}
      />

      <Dialog
        open={Boolean(pendingActivate)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingActivate(null);
            setActivateError(null);
            setActivateNote("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activate {pendingActivate?.name}?</DialogTitle>
            <DialogDescription>
              Activation makes the template assignable to attempts. The BE pre-flights the blueprint
              — if any item lacks approved questions, activation will fail loudly.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="activate-note">Note (optional)</Label>
            <Textarea
              id="activate-note"
              value={activateNote}
              onChange={(e) => setActivateNote(e.target.value)}
              placeholder="First activation for the 2026 admissions cycle."
              maxLength={500}
              rows={2}
            />
          </div>
          {activateError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {activateError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={activateMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={() => void handleConfirmActivate()}
              disabled={activateMutation.isPending}
            >
              {activateMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Activating…
                </>
              ) : (
                <>
                  <CheckCircle2 aria-hidden="true" className="size-4" />
                  Activate template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(pendingArchive)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingArchive(null);
            setArchiveError(null);
            setArchiveReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive {pendingArchive?.name}?</DialogTitle>
            <DialogDescription>
              Archived templates can't be assigned to new attempts. Existing attempts remain valid
              because each attempt snapshots its question set at assignment time.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="archive-template-reason">Reason (3–500 chars)</Label>
            <Textarea
              id="archive-template-reason"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Replaced by the 2026-edition blueprint."
              maxLength={500}
              rows={3}
            />
          </div>
          {archiveError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {archiveError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={archiveMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmArchive()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Archiving…
                </>
              ) : (
                <>
                  <Trash2 aria-hidden="true" className="size-4" />
                  Archive template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateRow({
  template,
  canWrite,
  onOpen,
  onActivate,
  onArchive,
}: {
  template: AssessmentTemplatePublic;
  canWrite: boolean;
  onOpen: (slug: string) => void;
  onActivate: (t: AssessmentTemplatePublic) => void;
  onArchive: (t: AssessmentTemplatePublic) => void;
}) {
  const status = mapTemplateStatusToPill(template.status);
  const isDraft = template.status === "draft";
  const isActive = template.status === "active";
  const isArchived = template.status === "archived";

  return (
    <TableRow>
      <TableCell>
        <button
          type="button"
          className="text-left hover:underline focus-visible:underline focus-visible:outline-none"
          onClick={() => onOpen(template.slug)}
        >
          <span className="text-foreground font-medium">{template.name}</span>
          {template.description ? (
            <span className="text-muted-foreground line-clamp-1 text-xs">
              {template.description}
            </span>
          ) : null}
        </button>
      </TableCell>
      <TableCell className="text-xs tabular-nums">
        {template.grade_band.min}–{template.grade_band.max}
      </TableCell>
      <TableCell className="text-xs uppercase">{template.language}</TableCell>
      <TableCell className="text-xs tabular-nums">{template.items.length}</TableCell>
      <TableCell className="text-right text-xs tabular-nums">{template.total_marks}</TableCell>
      <TableCell className="text-xs capitalize">
        {template.generation_mode.replace("_", " ")}
      </TableCell>
      <TableCell>
        <StatusPill status={status} />
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={`Open actions for ${template.name}`}
            >
              <MoreHorizontal aria-hidden="true" className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onOpen(template.slug)}>
              <Pencil aria-hidden="true" className="size-3.5" />
              Open blueprint
            </DropdownMenuItem>
            {canWrite && isDraft ? (
              <DropdownMenuItem onSelect={() => onActivate(template)}>
                <CheckCircle2 aria-hidden="true" className="size-3.5" />
                Activate…
              </DropdownMenuItem>
            ) : null}
            {canWrite && !isArchived ? (
              <DropdownMenuItem
                onSelect={() => onArchive(template)}
                disabled={isActive && template.items.length === 0}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 aria-hidden="true" className="size-3.5" />
                Archive…
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

function extractMessage(raw: unknown, fallback: string): string {
  if (raw instanceof ApiError && raw.message) return raw.message;
  if (raw instanceof Error && raw.message) return raw.message;
  return fallback;
}
